import React, { useState } from 'react';
import { 
  Stack, Box, Typography, IconButton, Chip, 
  InputBase, alpha, useTheme,
  Menu, MenuItem, ListItemIcon, ListItemText,
  Button
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import SortIcon from '@mui/icons-material/Sort';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ModernCard from './shared/ModernCard';
import BonusSummaryBar from './BonusSummaryBar';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import EventIcon from '@mui/icons-material/Event';
import PublicIcon from '@mui/icons-material/Public';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const SORT_OPTIONS = [
  { value: 'amount', label: 'Bonus Amount', icon: <AttachMoneyIcon /> },
  { value: 'name', label: 'Bank Name', icon: <AccountBalanceIcon /> },
  { value: 'successRate', label: 'Success Rate', icon: <TrendingUpIcon /> },
  { value: 'expiration', label: 'Expiration Date', icon: <EventIcon /> }
];

const STATES = [
  'Nationwide',
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

const SearchBarCard = ({ searchValue, onSearch }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.12)} 100%)`,
        width: '100%',
        height: '56px',
        margin: 0,
        padding: '8px 16px',
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
      }}
    >
      <SearchIcon 
        sx={{ 
          color: theme.palette.primary.main,
          fontSize: '20px',
          opacity: 0.9,
          mr: 1.5,
          flexShrink: 0
        }} 
      />
      <InputBase
        fullWidth
        placeholder="Search banks..."
        value={searchValue}
        onChange={(e) => onSearch(e.target.value)}
        sx={{
          flex: 1,
          height: '40px',
          '& input': {
            height: '40px',
            fontSize: '0.9rem',
            color: theme.palette.text.primary,
            padding: '0',
            '&::placeholder': {
              color: alpha(theme.palette.primary.main, 0.7),
              opacity: 0.8,
            },
          },
        }}
      />
    </Box>
  );
};

const TotalCompletedCard = ({ banks }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  
  const totalBanks = banks.length;
  const completedBanks = banks.filter(bank => bank.completed).length;
  
  return (
    <Box
      sx={{ 
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.12)} 100%)`,
        width: '100%',
        margin: 0,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
        borderRadius: 2,
        height: '52px',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Stack 
        direction="row" 
        sx={{ 
          width: '100%', 
          px: 3,
          justifyContent: 'space-between'
        }}
      >
        <Stack alignItems="center" direction="row" spacing={1}>
          <Typography variant="body2" sx={{ color: alpha(theme.palette.primary.main, 0.9), fontWeight: 500 }}>
            Completed:
          </Typography>
          <Typography sx={{ color: theme.palette.primary.main, fontWeight: 600, fontSize: '0.95rem' }}>
            {completedBanks}
          </Typography>
        </Stack>
        <Stack alignItems="center" direction="row" spacing={1}>
          <Typography variant="body2" sx={{ color: alpha(theme.palette.primary.main, 0.9), fontWeight: 500 }}>
            Available:
          </Typography>
          <Typography sx={{ color: theme.palette.primary.main, fontWeight: 600, fontSize: '0.95rem' }}>
            {totalBanks}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
};

const FilterControlsCard = ({ handleSortClick, handleFilterClick, selectedState }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box
      sx={{
        p: 1.5,
        width: '100%',
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.12)} 100%)`,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
        borderRadius: 2,
      }}
    >
      <Stack direction="row" spacing={1} justifyContent="space-between">
        <Button
          onClick={handleSortClick}
          startIcon={<SortIcon sx={{ color: theme.palette.primary.main }} />}
          fullWidth
          sx={{
            justifyContent: 'flex-start',
            color: theme.palette.primary.main,
            backgroundColor: alpha(theme.palette.primary.main, 0.04),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
            borderRadius: 1.5,
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
            },
          }}
        >
          Sort
        </Button>
        <Button
          onClick={handleFilterClick}
          startIcon={<LocationOnIcon sx={{ color: selectedState ? theme.palette.primary.main : theme.palette.primary.main }} />}
          fullWidth
          sx={{
            justifyContent: 'flex-start',
            color: theme.palette.primary.main,
            backgroundColor: selectedState ? alpha(theme.palette.primary.main, 0.08) : alpha(theme.palette.primary.main, 0.04),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
            borderRadius: 1.5,
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
            },
          }}
        >
          {selectedState || 'Location'}
        </Button>
      </Stack>
    </Box>
  );
};

const MobileBankTable = ({ 
  banks, 
  onBankClick,
  onBookmarkToggle,
  onHideToggle,
  onSearch,
  onSort,
  onFilter,
  onStateClick,
  searchValue = '',
  bookmarkedBanks = [],
  hiddenBanks = [],
  loading,
  isAdmin,
  selectedState,
  userBonuses = {}
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [expandedBankId, setExpandedBankId] = useState(null);

  const handleSortClick = (event) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleSortClose = () => {
    setSortAnchorEl(null);
  };

  const handleSortOptionClick = (option) => {
    onSort(option.value);
    handleSortClose();
  };

  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleStateSelect = (state) => {
    onStateClick(state);
    handleFilterClose();
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case 'easy':
        return {
          color: isDark ? '#34D399' : '#059669',
          bgColor: isDark ? alpha('#34D399', 0.12) : alpha('#059669', 0.08)
        };
      case 'hard':
        return {
          color: isDark ? '#F87171' : '#DC2626',
          bgColor: isDark ? alpha('#F87171', 0.12) : alpha('#DC2626', 0.08)
        };
      default: // medium
        return {
          color: isDark ? '#FBBF24' : '#D97706',
          bgColor: isDark ? alpha('#FBBF24', 0.12) : alpha('#D97706', 0.08)
        };
    }
  };

  const renderStateChips = (states) => (
    <Stack direction="row" spacing={1} flexWrap="wrap">
      {states.map((state) => (
        <Chip
          key={state}
          label={state}
          onClick={(e) => {
            e.stopPropagation();
            onStateClick(state);
          }}
          sx={{
            cursor: 'pointer',
            bgcolor: state === selectedState
              ? theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.2) : alpha(theme.palette.primary.main, 0.15)
              : state.toLowerCase() === 'nationwide'
                ? theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.08) : alpha(theme.palette.primary.main, 0.06)
                : theme.palette.mode === 'dark' ? alpha(theme.palette.secondary.main, 0.08) : alpha(theme.palette.secondary.main, 0.06),
            color: state === selectedState
              ? theme.palette.primary.main
              : state.toLowerCase() === 'nationwide'
                ? theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.9) : theme.palette.primary.main
                : theme.palette.mode === 'dark' ? alpha(theme.palette.secondary.main, 0.9) : theme.palette.secondary.main,
            border: '1px solid',
            borderColor: state === selectedState
              ? theme.palette.primary.main
              : state.toLowerCase() === 'nationwide'
                ? theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.15) : alpha(theme.palette.primary.main, 0.12)
                : theme.palette.mode === 'dark' ? alpha(theme.palette.secondary.main, 0.15) : alpha(theme.palette.secondary.main, 0.12),
            '&:hover': {
              bgcolor: state === selectedState
                ? theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.25) : alpha(theme.palette.primary.main, 0.2)
                : state.toLowerCase() === 'nationwide'
                  ? theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.15) : alpha(theme.palette.primary.main, 0.12)
                  : theme.palette.mode === 'dark' ? alpha(theme.palette.secondary.main, 0.15) : alpha(theme.palette.secondary.main, 0.12),
            },
            mb: 1,
          }}
        />
      ))}
    </Stack>
  );

  return (
    <Stack spacing={0} sx={{ width: '100%', pb: 2, mt: '56px' }}>
      <Box sx={{ mb: 1 }} className="mobile-search-section">
        <SearchBarCard searchValue={searchValue} onSearch={onSearch} />
      </Box>
      <Box sx={{ mb: 1 }} className="mobile-stats-section">
        <TotalCompletedCard banks={banks} />
      </Box>
      <Box sx={{ mb: 1 }}>
        <FilterControlsCard 
          handleSortClick={handleSortClick}
          handleFilterClick={handleFilterClick}
          selectedState={selectedState}
        />
      </Box>

      {/* Sort Menu */}
      <Menu
        anchorEl={sortAnchorEl}
        open={Boolean(sortAnchorEl)}
        onClose={handleSortClose}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 200,
            maxWidth: '90vw',
            boxShadow: theme.shadows[4],
            borderRadius: 2,
          }
        }}
      >
        {SORT_OPTIONS.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => handleSortOptionClick(option)}
            sx={{
              py: 1.5,
              px: 2,
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              {option.icon}
            </ListItemIcon>
            <ListItemText primary={option.label} />
          </MenuItem>
        ))}
      </Menu>

      {/* Location Filter Menu */}
      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={handleFilterClose}
        PaperProps={{
          sx: {
            mt: 1,
            width: '90vw',
            maxHeight: '70vh',
            boxShadow: theme.shadows[4],
            borderRadius: 2,
          }
        }}
      >
        <MenuItem
          onClick={() => handleStateSelect('')}
          sx={{
            py: 1.5,
            px: 2,
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
            },
          }}
        >
          <ListItemIcon>
            <PublicIcon />
          </ListItemIcon>
          <ListItemText primary="All States" />
        </MenuItem>
        {STATES.map((state) => (
          <MenuItem
            key={state}
            onClick={() => handleStateSelect(state)}
            selected={selectedState === state}
            sx={{
              py: 1.5,
              px: 2,
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
              },
              '&.Mui-selected': {
                backgroundColor: alpha(theme.palette.primary.main, 0.12),
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.16),
                },
              },
            }}
          >
            <ListItemIcon>
              <LocationOnIcon />
            </ListItemIcon>
            <ListItemText primary={state} />
          </MenuItem>
        ))}
      </Menu>

      {/* Bank List */}
      <Stack spacing={1} sx={{ width: '100%' }}>
        {banks.map((bank) => {
          const isBookmarked = bookmarkedBanks.includes(bank.id);
          const isHidden = hiddenBanks.includes(bank.id);
          const difficultyColors = getDifficultyColor(bank.difficulty);
          const userBonus = userBonuses[bank.id];
          const isTracked = userBonus && userBonus.status === 'active';
          
          return (
            <ModernCard
              key={bank.id}
              highlight={isBookmarked}
              onClick={() => onBankClick(bank)}
              className={index === 2 ? 'mobile-bank-card' : ''}
              sx={{
                p: 1.5,
                borderRadius: 2,
                background: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: 'none',
                '&:hover': {
                  background: theme.palette.action.hover,
                },
                ...(isBookmarked && {
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.12)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                }),
              }}
            >
              <Stack spacing={1}>
                {/* Top Row: Bank Name, Actions, and Amount */}
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  {/* Left: Bonus Amount */}
                  <Typography 
                    variant="subtitle1" 
                    color="primary"
                    sx={{ 
                      fontWeight: 600,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    ${bank.bonusAmount}
                  </Typography>

                  {/* Middle: Bank Name */}
                  <Stack direction="column" spacing={0.5} sx={{ flex: 1, minWidth: 0, px: 2 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        color: theme.palette.text.primary
                      }}
                    >
                      {bank.name}
                    </Typography>
                    {bank.url && (
                      <Button
                        size="small"
                        variant="outlined"
                        endIcon={<OpenInNewIcon sx={{ fontSize: '0.75rem' }} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(bank.url, '_blank', 'noopener,noreferrer');
                        }}
                        className={index === 2 ? 'mobile-bonus-link' : ''}
                        sx={{
                          alignSelf: 'flex-start',
                          fontSize: '0.7rem',
                          padding: '2px 8px',
                          borderColor: alpha(theme.palette.primary.main, 0.3),
                          color: theme.palette.primary.main,
                          '&:hover': {
                            borderColor: theme.palette.primary.main,
                            backgroundColor: alpha(theme.palette.primary.main, 0.05),
                          }
                        }}
                      >
                        Bonus Link
                      </Button>
                    )}
                  </Stack>

                  {/* Right: Actions and Difficulty */}
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Chip
                      label={bank.difficulty || 'Medium'}
                      size="small"
                      sx={{
                        height: 20,
                        backgroundColor: difficultyColors.bgColor,
                        color: difficultyColors.color,
                        '& .MuiChip-label': { px: 1, fontSize: '0.75rem' },
                        fontWeight: 500,
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onHideToggle(bank);
                      }}
                      sx={{
                        padding: 0.5,
                        color: isHidden ? theme.palette.error.main : theme.palette.text.secondary,
                      }}
                    >
                      {isHidden ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onBookmarkToggle(bank);
                      }}
                      sx={{
                        padding: 0.5,
                        color: isBookmarked ? theme.palette.primary.main : theme.palette.text.secondary,
                      }}
                    >
                      {isBookmarked ? <BookmarkIcon fontSize="small" /> : <BookmarkBorderIcon fontSize="small" />}
                    </IconButton>
                  </Stack>
                </Stack>

                {/* Bottom Row: States, DD Amount, Success Rate */}
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  {/* Left: States */}
                  <Box sx={{ mt: 2 }}>
                    {renderStateChips(bank.states)}
                  </Box>

                  {/* Right: DD Amount and Success Rate */}
                  <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                      Direct Deposit: ${bank.directDepositAmount}
                    </Typography>
                    {(bank.totalAttempts > 0 || bank.successfulCompletions > 0 || bank.failedCompletions > 0) && (
                      <Chip
                        label={`${bank.successRate}%`}
                        size="small"
                        sx={{
                          height: 20,
                          backgroundColor: alpha(theme.palette.success.main, 0.08),
                          color: theme.palette.success.main,
                          '& .MuiChip-label': { px: 1, fontSize: '0.75rem' },
                        }}
                      />
                    )}
                  </Stack>
                </Stack>

                {/* Time Info Row */}
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 0.5 }}>
                  {/* Status */}
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    {userBonuses[bank.id] && userBonuses[bank.id].status !== 'completed' ? (
                      <Chip
                        label="In Progress"
                        size="small"
                        sx={{
                          backgroundColor: alpha(theme.palette.primary.main, 0.15),
                          color: theme.palette.primary.main,
                          fontWeight: 600,
                          fontSize: '0.7rem',
                          height: '20px',
                        }}
                      />
                    ) : (
                      <Chip
                        label="Not Started"
                        size="small"
                        sx={{
                          backgroundColor: alpha(theme.palette.text.secondary, 0.1),
                          color: theme.palette.text.secondary,
                          fontWeight: 600,
                          fontSize: '0.7rem',
                          height: '20px',
                        }}
                      />
                    )}
                  </Stack>

                  {/* Expiration Date */}
                  {bank.expiration && (
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: new Date(bank.expiration) < new Date()
                            ? theme.palette.error.main
                            : theme.palette.text.secondary,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        <span style={{ color: isDark ? alpha('#94A3B8', 0.8) : '#64748B' }}>
                          Expires:
                        </span>
                        {(() => {
                          // Format date without timezone conversion
                          if (typeof bank.expiration === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(bank.expiration)) {
                            const [year, month, day] = bank.expiration.split('-');
                            return new Date(year, month - 1, day).toLocaleDateString('en-US', {
                              year: '2-digit',
                              month: '2-digit',
                              day: '2-digit'
                            });
                          }
                          // Parse as UTC to avoid timezone issues
                          const date = new Date(bank.expiration);
                          return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()).toLocaleDateString('en-US', {
                            year: '2-digit',
                            month: '2-digit',
                            day: '2-digit'
                          });
                        })()}
                      </Typography>
                    </Stack>
                  )}

                  {/* Expand Button - Only show if bonus is being tracked */}
                  {isTracked && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedBankId(expandedBankId === bank.id ? null : bank.id);
                      }}
                      sx={{
                        padding: 0.5,
                        color: theme.palette.text.secondary,
                        transform: expandedBankId === bank.id ? 'rotate(180deg)' : 'none',
                        transition: 'transform 0.2s ease-in-out',
                        ml: 1
                      }}
                    >
                      <KeyboardArrowDownIcon fontSize="small" />
                    </IconButton>
                  )}
                </Stack>

                {/* Expanded Details */}
                {expandedBankId === bank.id && isTracked && (
                  <Box sx={{ 
                    mt: 1.5, 
                    pt: 1.5, 
                    borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    background: alpha(theme.palette.primary.main, 0.04),
                    borderRadius: 1,
                    p: 1.5
                  }}>
                    <Stack spacing={1.5}>
                      {/* Start Date */}
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: alpha(theme.palette.primary.main, 0.7),
                            fontWeight: 500
                          }}
                        >
                          Started:
                        </Typography>
                        <Typography 
                          variant="body2" 
                          color="text.primary"
                          sx={{ fontWeight: 500 }}
                        >
                          {userBonus.dateStarted.toDate().toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </Typography>
                      </Stack>

                      {/* Expected Payout Date */}
                      {userBonus.expectedPayoutDate && (
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: alpha(theme.palette.primary.main, 0.7),
                              fontWeight: 500
                            }}
                          >
                            Est. Payout:
                          </Typography>
                          <Typography 
                            variant="body2" 
                            color="text.primary"
                            sx={{ fontWeight: 500 }}
                          >
                            {userBonus.expectedPayoutDate.toDate().toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </Typography>
                        </Stack>
                      )}
                    </Stack>
                  </Box>
                )}
              </Stack>
            </ModernCard>
          );
        })}
      </Stack>
    </Stack>
  );
};

export default MobileBankTable; 